import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import {
  GeneralCatalogItem,
  RequestIndicatorTypes,
} from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { colors, DisplayItem, FormHookResult } from 'design-system';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { AnyAction } from 'redux';

import {
  CONTRACTOR_DATA_EMISSION_STEP,
  CUSTOMER_BIRTH_DATE,
  CUSTOMER_NAME,
  EMAIL,
  FINANCING,
  LASTNAME,
  PHONE,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';
import { getCatalogToOptions } from 'src/functions/equifax';
import EmissionDataFields from 'src/models/helperTypes/EmissionDataFields';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';
import {
  REQUEST_UPDATE_EMISSION_DATA,
  REQUEST_UPLOAD_CEDULA_FILE,
  REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE,
} from 'src/store/emissionPolicy/requestIdentifiers';

import { states as staticStates } from 'src/data/statesCr.json';
import { cities as staticCities } from 'src/data/citiesCr.json';
import { townships as staticTownships } from 'src/data/townshipsCr.json';
import useAddressInformation from './useAddressInformation';
import usePersistedAddressInformation from './usePersistedAddressInformation';
import useCivilInformation from '../NaturalPersonData/hooks/useCivilInformation';
import UsePersonIdData from '../NaturalPersonData/hooks/usePersonIdData';
import useBaseWrapperStyles from '../useBaseWrapperStyles';

const useStyles = makeStyles({
  title: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: '25px',
    color: colors.madison,
  },
});

type Props = {
  dataFields: EmissionDataFields;
  personData: PersonDataResponse;
  isCollectiveQuotation: boolean;
  countries: GeneralCatalogItem[];
  countryStates: GeneralCatalogItem[];
  cities: GeneralCatalogItem[];
  townships: GeneralCatalogItem[];
  birthPlaces: GeneralCatalogItem[];
  civilStatus: GeneralCatalogItem[];
  restrictedBirthPlaces: GeneralCatalogItem[];
  getCountriesAction: () => unknown;
  getOccupationsAction?: () => unknown;
  getCountryStatesAction: () => unknown;
  getBirthPlacesAction: () => unknown;
  getRestrictedBirthPlacesAction: () => unknown;
  getCitiesAction: (value: string | number) => Promise<AnyAction>;
  getTownshipsAction: (value: string | number) => Promise<AnyAction>;
  setDataFieldsAction: (dataFields: EmissionDataFields) => unknown;
  onSubmit: () => void;
  resetRequestStatus: (id: string, status: RequestIndicatorTypes) => unknown;
  birthPlaceValue: string;
  personIsInvalid: boolean;
  cedulaFileName: string;
  cedulaFileBase64: string;
  cedulaOptionalFileName: string;
  cedulaOptionalFileBase64: string;
  uploadCedulaAction: (fileName: string, base64Image: string) => unknown;
  uploadCedulaOptionalAction: (
    fileName: string,
    base64Image: string,
  ) => unknown;
  resetCedulaFilesAction: (fileId: string) => unknown;
  getInputProps: FormHookResult;
  setSelectedOrigin: Dispatch<SetStateAction<string>>;
};

type HookResult = {
  addressInformation: ReturnType<typeof useAddressInformation>;
  civilInformation: ReturnType<typeof useCivilInformation>;
  personIdData: ReturnType<typeof UsePersonIdData>;
  classes: ReturnType<typeof useStyles>;
  isMobile: boolean;
  wrapperClasses: ReturnType<typeof useBaseWrapperStyles>;
  requestingUpdateDataStatus: RequestIndicatorTypes;
  cedulaFileStatus: RequestIndicatorTypes;
  cedulaOptionalFileStatus: RequestIndicatorTypes;
  today: Date;
  twoMonthsAhead: Date;
  openValidationModal: boolean;
  setOpenValidationModal: Dispatch<SetStateAction<boolean>>;
  openRestrictedCountryModal: boolean;
  setOpenRestrictedCountryModal: Dispatch<SetStateAction<boolean>>;
  showValiationAlert: boolean;
  setShowValiationAlert: Dispatch<SetStateAction<boolean>>;
  cityOptions: DisplayItem[];
  setCityOptions: Dispatch<SetStateAction<DisplayItem[]>>;
  townshipOptions: DisplayItem[];
  setTownshipOptions: Dispatch<SetStateAction<DisplayItem[]>>;
};

export default function usePersonDataCommon({
  dataFields,
  personData,
  isCollectiveQuotation,
  countries,
  countryStates,
  cities,
  townships,
  birthPlaces,
  civilStatus,
  restrictedBirthPlaces,
  getCountriesAction,
  getOccupationsAction,
  getCountryStatesAction,
  getBirthPlacesAction,
  getRestrictedBirthPlacesAction,
  getCitiesAction,
  getTownshipsAction,
  setDataFieldsAction,
  onSubmit,
  resetRequestStatus,
  birthPlaceValue,
  personIsInvalid,
  cedulaFileName,
  cedulaFileBase64,
  cedulaOptionalFileName,
  cedulaOptionalFileBase64,
  uploadCedulaAction,
  uploadCedulaOptionalAction,
  resetCedulaFilesAction,
  getInputProps,
  setSelectedOrigin,
}: Props): HookResult {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const wrapperClasses = useBaseWrapperStyles();
  const requestingUpdateDataStatus = useNetworkObserver(
    REQUEST_UPDATE_EMISSION_DATA,
  );
  const cedulaFileStatus = useNetworkObserver(REQUEST_UPLOAD_CEDULA_FILE);
  const cedulaOptionalFileStatus = useNetworkObserver(
    REQUEST_UPLOAD_CEDULA_OPTIONAL_FILE,
  );
  const today = new Date();
  const twoMonthsAhead = new Date(today);
  twoMonthsAhead.setDate(today.getDate() - 45);

  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [openRestrictedCountryModal, setOpenRestrictedCountryModal] =
    useState(false);
  const [showValiationAlert, setShowValiationAlert] = useState(false);
  const [cityOptions, setCityOptions] = useState<DisplayItem[]>([]);
  const [townshipOptions, setTownshipOptions] = useState<DisplayItem[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getCountriesAction();
    getOccupationsAction?.();
    getCountryStatesAction();
    getBirthPlacesAction();
    getRestrictedBirthPlacesAction();
  }, [
    getCountriesAction,
    getOccupationsAction,
    getCountryStatesAction,
    getBirthPlacesAction,
    getRestrictedBirthPlacesAction,
  ]);

  usePersistedAddressInformation({
    dataFields,
    countryStates,
    cities,
    getCitiesAction,
    getTownshipsAction,
    setDataFieldsAction,
  });

  useEffect(() => {
    const state = staticStates.find(
      (item) => item.code === dataFields.addressState,
    );
    setCityOptions(getCatalogToOptions(staticCities, state?.id));
  }, [dataFields.addressState]);

  useEffect(() => {
    const state = staticStates.find(
      (item) => item.code === dataFields.addressState,
    );
    const citiesForState = staticCities.filter(
      (item) => item.parentId === state?.id,
    );
    const city = citiesForState.find(
      (item) => item.code === dataFields.addressCity,
    );
    setTownshipOptions(getCatalogToOptions(staticTownships, city?.id));
  }, [dataFields.addressCity, dataFields.addressState]);

  useEffect(() => {
    setOpenValidationModal(!personData.isValidPerson);
    setShowValiationAlert(!personData.isValidPerson);
  }, [personData]);

  useEffect(() => {
    if (requestingUpdateDataStatus === 'success') {
      resetRequestStatus(REQUEST_UPDATE_EMISSION_DATA, 'initial');
      trackEvent({
        eventName: CONTRACTOR_DATA_EMISSION_STEP,
        eventProperties: {
          [CUSTOMER_NAME]: dataFields.names,
          [LASTNAME]: dataFields.lastNames,
          [CUSTOMER_BIRTH_DATE]: dataFields.birthday,
          [EMAIL]: dataFields.email,
          [PHONE]: dataFields.phoneNumber,
          [FINANCING]: dataFields.creditorType,
        },
      });
      trackCurrentStep(CONTRACTOR_DATA_EMISSION_STEP);
      onSubmit();
    }
  }, [requestingUpdateDataStatus, dataFields, onSubmit, resetRequestStatus]);

  useEffect(() => {
    if (restrictedBirthPlaces.find((place) => place.code === birthPlaceValue)) {
      setShowValiationAlert(true);
      setOpenRestrictedCountryModal(true);
    } else {
      setShowValiationAlert(false);
      setOpenRestrictedCountryModal(false);
    }
  }, [birthPlaceValue, restrictedBirthPlaces]);

  const originTypeValue = getInputProps('originType').value;
  useEffect(() => {
    setSelectedOrigin(originTypeValue as string);
  }, [originTypeValue, setSelectedOrigin]);

  const addressInformation = useAddressInformation({
    countries,
    cities,
    countryStates,
    townships,
    getCitiesAction,
    getTownshipsAction,
  });
  const civilInformation = useCivilInformation({ birthPlaces, civilStatus });
  const personIdData = UsePersonIdData({
    uploadCedulaAction,
    cedulaFileName,
    cedulaFileBase64,
    cedulaFileStatus,
    uploadCedulaOptionalAction,
    cedulaOptionalFileName,
    cedulaOptionalFileBase64,
    cedulaOptionalFileStatus,
    resetCedulaFilesAction,
    disable: personIsInvalid,
    isCollectiveQuotation,
  });

  return {
    addressInformation,
    civilInformation,
    personIdData,
    classes,
    isMobile,
    wrapperClasses,
    requestingUpdateDataStatus,
    cedulaFileStatus,
    cedulaOptionalFileStatus,
    today,
    twoMonthsAhead,
    openValidationModal,
    setOpenValidationModal,
    openRestrictedCountryModal,
    setOpenRestrictedCountryModal,
    showValiationAlert,
    setShowValiationAlert,
    cityOptions,
    setCityOptions,
    townshipOptions,
    setTownshipOptions,
  };
}
